import { useEffect, useState } from 'react'
import MediaQuery from 'react-responsive'
import { Button, Drawer, Grid } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { changeSearchText, searchFundersByParams } from 'store/actions/search'
import { selectCardView } from 'store/selectors/pageSettings'
import { CardViewEnum } from 'store/reducers/pageSettings'

import Facets from 'components/Facets'
import SearchHeader from 'components/SearchHeader'
import Filters from 'components/Filters'
import Pagination from 'components/SearchPagination'
import CardViewChanger from 'components/CardViewChanger'
import SearchResults from 'components/SearchResults'

import { toggleCardView } from 'store/actions/pageSettings'
import { selectAllFilters, selectIsResetButtonDisabled } from 'store/selectors/filters'
import { resetFilters, setFilterByKey } from 'store/actions/filters'
import { selectAreFacetsAtIntialState } from 'store/selectors/facets'
import { getAllFacetsAndSyncURL } from 'store/actions/facets'
import { getPipelines } from 'store/actions/pipeline'
import { getPipelineStages } from 'store/actions/pipelineStages'
import { selectPaginationState } from 'store/selectors/searchPagination'
import { selectResultCount } from 'store/selectors/search'
import PageInfo from 'components/Pagination/components/PageInfo'
import { selectHasExecutePermissions } from 'store/selectors/user'

import { useTitle } from 'hooks'
import { useHotjar } from 'utils/hotjar'
import { trackPageView } from 'utils/mixpanel'
import SearchBanner from 'components/SearchBanner'

const Search = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const dispatch = useDispatch()
  const { language, openToRequests, sortBy, medianGiftMin, medianGiftMax } =
    useSelector(selectAllFilters)
  const isResetButtonDisabled = useSelector(selectIsResetButtonDisabled)

  const [state, setState] = useState({
    showMobileDrawer: false
  })

  const { text: searchText, results: searchResults, loading } = useSelector(({ search }) => search)
  const cardView = useSelector(selectCardView)
  const areFacetsAtInitialState = useSelector(selectAreFacetsAtIntialState)
  const hasExecutePermissions = useSelector(selectHasExecutePermissions)

  const { viewsPerPage, pageNumber } = useSelector(selectPaginationState)
  const resultCount = useSelector(selectResultCount)

  useTitle(t => t.search.mainTitle)

  useHotjar()

  useEffect(() => {
    trackPageView()
  }, [])

  useEffect(() => {
    if (hasExecutePermissions) {
      dispatch(getPipelines())
      dispatch(getPipelineStages())
    }
  }, [hasExecutePermissions])

  useEffect(() => {
    const fetchFacets = async () => {
      if (areFacetsAtInitialState) {
        // fetch facets before searching, because search results will update facet docCount
        await getAllFacetsAndSyncURL()
        dispatch(searchFundersByParams())
      }
    }
    fetchFacets()
  }, [areFacetsAtInitialState, dispatch])

  const handleReset = () => {
    dispatch(changeSearchText(''))
    dispatch(resetFilters())
  }

  return (
    <>
      <MediaQuery query="(max-width: 767px)">
        <Drawer
          open={state.showMobileDrawer}
          ModalProps={{ className: 'App Search-page__filters-mobile' }}
          onClose={() => setState({ showMobileDrawer: false })}
        >
          <Facets />

          <Filters
            language={language}
            openToRequests={openToRequests}
            sortBy={sortBy}
            onChange={({ key, value }) => dispatch(setFilterByKey({ [key]: value }))}
            showOnlySortBy={false}
            hideSortBy={true}
          />
        </Drawer>
      </MediaQuery>

      <Grid container className="main">
        <Grid size={{ xs: 12 }}>
          <SearchBanner />
        </Grid>

        <MediaQuery query="(min-width: 768px)">
          <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
            <Facets />
          </Grid>
        </MediaQuery>

        <Grid size={{ xs: 12, sm: 8, lg: 9 }} className="search-container">
          <SearchHeader
            onReset={handleReset}
            disableReset={isResetButtonDisabled}
            searchText={searchText}
          />

          <MediaQuery query="(min-width: 768px)">
            <Filters
              language={language}
              openToRequests={openToRequests}
              sortBy={sortBy}
              onChange={({ key, value }) => dispatch(setFilterByKey({ [key]: value }))}
              showOnlySortBy={false}
              hideSortBy={false}
            />
          </MediaQuery>

          <div>
            <Grid container>
              <Grid size={{ xs: 12, md: 10 }}>
                <div className="tw-flex tw-justify-between tw-items-center">
                  {!!resultCount && (
                    <PageInfo
                      className={'Search-page__results tw-flex-none'}
                      resultCount={resultCount}
                      viewsPerPage={viewsPerPage}
                      pageNumber={pageNumber}
                    />
                  )}
                  <div className="tw-block md:tw-hidden tw-flex tw-w-64 tw-justify-end">
                    <Filters
                      language={language}
                      openToRequests={openToRequests}
                      sortBy={sortBy}
                      onChange={({ key, value }) => dispatch(setFilterByKey({ [key]: value }))}
                      showOnlySortBy={true}
                      hideSortBy={false}
                    />
                    <div className="tw-mx-2">
                      <Button
                        className="Search-page__mobile-filters-button tw-ml-4"
                        onClick={() => setState({ showMobileDrawer: true })}
                        variant="text"
                      >
                        {t.search.filterMobile}
                      </Button>
                    </div>
                  </div>
                </div>
              </Grid>

              <MediaQuery query="(min-width: 769px)">
                <Grid size={{ md: 2 }}>
                  <CardViewChanger
                    onClick={() => dispatch(toggleCardView())}
                    expandedActive={cardView === CardViewEnum.EXPANDED ? 'active' : ''}
                    collapsedActive={cardView === CardViewEnum.COLLAPSED ? 'active' : ''}
                  />
                </Grid>
              </MediaQuery>
            </Grid>
          </div>

          <Grid container className="Search-page__results-container">
            <SearchResults loading={loading} results={searchResults} />
            {searchResults.length ? (
              <Grid container>
                <div className="tw-w-full">
                  <MediaQuery query="(min-width: 990px)">
                    <Pagination pagerSize={5} />
                  </MediaQuery>
                  <MediaQuery query="(max-width: 991px)">
                    <Pagination pagerSize={3} />
                  </MediaQuery>
                </div>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

export default Search
